import { useEffect, useRef, useState } from 'react';
import { useApiKey } from '@/stores/apiKey.store';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || 'https://api.satutim.id';
const ENDPOINT = `${API_BASE}/api/v1/ai/semantic-search`;
const DEBOUNCE_MS = 400;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 hari
const CACHE_PREFIX = 'semantic:';

export interface SemanticHit {
    operationId: string;
    score: number;
    summary: string;
    method: string;
    path: string;
    tag: string;
}

interface State {
    results: SemanticHit[];
    busy: boolean;
    error: string | null;
}

function hashQuery(q: string): string {
    // Stable lightweight hash — djb2 (good enough untuk cache key)
    let h = 5381;
    for (let i = 0; i < q.length; i++) {
        h = ((h << 5) + h) ^ q.charCodeAt(i);
    }
    return (h >>> 0).toString(36);
}

interface CacheRecord {
    ts: number;
    data: SemanticHit[];
}

function readCache(key: string): SemanticHit[] | null {
    try {
        const raw = sessionStorage.getItem(CACHE_PREFIX + key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CacheRecord;
        if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
        return Array.isArray(parsed.data) ? parsed.data : null;
    } catch {
        return null;
    }
}

function writeCache(key: string, data: SemanticHit[]) {
    try {
        sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ ts: Date.now(), data }));
    } catch {
        // Quota / private mode — soft fail
    }
}

export function useSemanticSearch(query: string): State {
    const apiKey = useApiKey((s) => s.key);
    const [state, setState] = useState<State>({ results: [], busy: false, error: null });
    const abortRef = useRef<AbortController | null>(null);
    const seqRef = useRef(0);

    useEffect(() => {
        const trimmed = query.trim();

        // Empty query → clear results, no error.
        if (!trimmed) {
            abortRef.current?.abort();
            setState({ results: [], busy: false, error: null });
            return;
        }

        if (!apiKey) {
            setState({ results: [], busy: false, error: 'Login untuk pakai semantic search' });
            return;
        }

        const key = hashQuery(trimmed);
        const cached = readCache(key);
        if (cached) {
            setState({ results: cached, busy: false, error: null });
            return;
        }

        // Debounce
        const seq = ++seqRef.current;
        setState((prev) => ({ ...prev, busy: true, error: null }));
        const timer = window.setTimeout(async () => {
            abortRef.current?.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            try {
                const res = await fetch(ENDPOINT, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${apiKey}`,
                    },
                    body: JSON.stringify({ query: trimmed, limit: 8 }),
                    signal: ctrl.signal,
                });
                if (!res.ok) {
                    const txt = await res.text().catch(() => '');
                    throw new Error(`HTTP ${res.status}${txt ? `: ${txt.slice(0, 120)}` : ''}`);
                }
                const json = await res.json() as { data?: SemanticHit[] };
                const data = Array.isArray(json?.data) ? json.data : [];
                if (seq !== seqRef.current) return; // stale
                writeCache(key, data);
                setState({ results: data, busy: false, error: null });
            } catch (err: any) {
                if (err?.name === 'AbortError') return;
                if (seq !== seqRef.current) return;
                setState({ results: [], busy: false, error: err?.message || 'Gagal cari endpoint' });
            }
        }, DEBOUNCE_MS);

        return () => {
            window.clearTimeout(timer);
        };
    }, [query, apiKey]);

    return state;
}
